'use client';

import * as React from 'react';
import { Menu } from '@base-ui/react/menu';

import { cn } from '../lib/utils';

/**
 * DropdownMenu — shadcn-style compound component built on Base UI's Menu
 * primitive. Provides full keyboard navigation, focus management, and
 * accessibility out of the box.
 *
 * Usage:
 *   <DropdownMenu>
 *     <DropdownMenuTrigger>Open</DropdownMenuTrigger>
 *     <DropdownMenuContent>
 *       <DropdownMenuLabel>Section</DropdownMenuLabel>
 *       <DropdownMenuItem>Item 1</DropdownMenuItem>
 *       <DropdownMenuItem render={<a href="..." />}>Link item</DropdownMenuItem>
 *     </DropdownMenuContent>
 *   </DropdownMenu>
 */

const DropdownMenu = Menu.Root;

const DropdownMenuTrigger = Menu.Trigger;

interface DropdownMenuContentProps extends Menu.Popup.Props {
  sideOffset?: number;
  align?: 'start' | 'center' | 'end';
}

function DropdownMenuContent({
  className,
  sideOffset = 6,
  align = 'start',
  children,
  ...props
}: DropdownMenuContentProps) {
  return (
    <Menu.Portal>
      <Menu.Positioner
        sideOffset={sideOffset}
        align={align}
        className="z-[100] outline-none"
      >
        <Menu.Popup
          className={cn(
            'min-w-[12rem] overflow-hidden rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-lg outline-none',
            className,
          )}
          {...props}
        >
          {children}
        </Menu.Popup>
      </Menu.Positioner>
    </Menu.Portal>
  );
}

// Hoist the Base UI Menu CSS layer so its defaults don't win over our z-index
// in certain bundler orderings (seen on Vite + React 18).

function DropdownMenuItem({
  className,
  ...props
}: Menu.Item.Props) {
  return (
    <Menu.Item
      className={cn(
        'group/item relative flex cursor-pointer select-none items-start gap-2.5 rounded-md px-2 py-2 text-sm outline-none transition-colors',
        'data-[highlighted]:bg-[var(--l3-violet-soft)] data-[highlighted]:text-[var(--l3-violet)]',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    />
  );
}

function DropdownMenuLabel({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'eyebrow px-2 py-1.5 opacity-50',
        className,
      )}
      {...props}
    />
  );
}

function DropdownMenuSeparator({
  className,
  ...props
}: Menu.Separator.Props) {
  return (
    <Menu.Separator
      className={cn('-mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  );
}

function DropdownMenuGroup({
  className,
  ...props
}: Menu.Group.Props) {
  return <Menu.Group className={className} {...props} />;
}

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuGroup,
};
